/**
 * One hook per endpoint. **Nothing polls.**
 *
 * The board loads on demand: a query only runs when the Load button enables it,
 * and the answer is then held for a day. That changes what this surface IS — it
 * is a report you pull, not a live wall display — and the UI says so rather than
 * showing a pulsing "LIVE" dot over day-old numbers.
 *
 * The trade is explicit: the most expensive panels here scan hundreds of millions
 * of documents server-side, so a board left open on a wall re-running them every
 * 10 s costs far more than it tells anyone. Anything genuinely per-second belongs
 * in an alert, not a page nobody is looking at.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, get, post } from './client'
import { useLoad } from './load'
import { useRange } from './range'
import * as S from './schemas'

/** One day. Matches the server-side cache so a reload inside the window is free. */
export const CACHE_MS = 24 * 60 * 60 * 1000

function make<T extends import('zod').ZodTypeAny>(key: string, path: string, schema: T) {
  return () => {
    const { loaded } = useLoad()
    return useQuery({
      queryKey: [key],
      queryFn: ({ signal }) => get(path, schema, signal),
      // Nothing fetches until Load is pressed.
      enabled: loaded,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: CACHE_MS,
      gcTime: CACHE_MS,
      placeholderData: (prev) => prev,
      retry: 2,
    })
  }
}

export const useHealth = make('health', '/metrics/health', S.Health)
export const useQueues = make('queues', '/metrics/queues', S.Queues)
export const useBudget = make('budget', '/metrics/budget', S.Budget)
export const useAttribution = make('attribution', '/metrics/attribution', S.Attribution)
export const useCoverage = make('coverage', '/metrics/coverage', S.Coverage)
export const useJobs = make('jobs', '/metrics/jobs', S.Jobs)
export const useEsHealth = make('esHealth', '/metrics/es/health', S.EsHealth)
/* The pipeline graph. A server-side constant with no I/O, so it answers even
 * when Redis is down — the board can always draw its skeleton and show "—" for
 * the figures, which is the state you most want it in during an outage. */
export const useTopology = make('topology', '/metrics/topology', S.Topology)
/* The classifier's own flow. Its queues live in a different Redis database, so
 * these can report "not configured" independently of the collector's. */
export const useClassifierTopology =
  make('clsTopology', '/metrics/classifier/topology', S.Topology)
export const useClassifierQueues =
  make('clsQueues', '/metrics/classifier/queues', S.ClassifierQueues)
/** Range-scoped. Changing the window is a new query key, so it fetches once for
 *  that window and is then held for a day like everything else. */
export function useEsActivity() {
  const { days } = useRange()
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['esActivity', days],
    queryFn: ({ signal }) => get(`/metrics/es/activity?days=${days}`, S.EsActivity, signal),
    enabled: loaded,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: CACHE_MS,
    gcTime: CACHE_MS,
    placeholderData: (prev) => prev,
    retry: 2,
  })
}
/**
 * Subscriber-tier activity — the companion to `useEsActivity` above, split by
 * the T0–T5 subscriber bands instead of just inserted/updated totals.
 *
 * A PLAIN CACHED READ, THE SAME SHAPE OF HOOK AS `useVidiqTiers`, not a poll:
 * the underlying scan touches 442M video rows and 28M channel rows, so this
 * panel reads a server-computed snapshot by default and only ever recomputes
 * when `useTierActivityRefresh` (below) is explicitly invoked. Polling this
 * on any interval would repeat the exact mistake the module doctrine at the
 * top of this file warns against — re-running an unbounded scan on a page
 * nobody is looking at. */
export function useTierActivity() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['tierActivity'],
    queryFn: ({ signal }) => get('/metrics/tier-activity', S.TierActivity, signal),
    enabled: loaded,
    /* POLLED ONLY WHILE A REFRESH IS ACTUALLY RUNNING, and this is the
     * distinction the doctrine above turns on. The warning is against polling
     * that RE-RUNS the unbounded scan; this GET does not run it — the scan is
     * the POST, and this reads a table of at most 48 rows. So the poll is
     * bounded by the work it is reporting on: it starts when a refresh is in
     * flight (started by ANY client, not just this tab) and stops the moment
     * it is not. An idle board still polls nothing. */
    refetchInterval: (q) =>
      q.state.data?.refresh?.in_progress ? 3000 : false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: CACHE_MS,
    gcTime: CACHE_MS,
    placeholderData: (prev) => prev,
    retry: 2,
  })
}

/**
 * Triggers the expensive tier-activity recompute. A MUTATION, not a query —
 * this is the one action on the board (besides Load/Refresh's cache-clear
 * POST in LoadBar.tsx) that asks the server to do work rather than read it.
 *
 * 409 IS NOT AN ERROR HERE. The endpoint returns it when a refresh is already
 * running — someone else's click, or a retry of our own — and the contract is
 * explicit that this means "in progress, here is the still-current cached
 * data", not "the request failed". Catching it inside `mutationFn` (rather
 * than letting it propagate and checking `mutation.isError` at the call site)
 * keeps that distinction enforced in exactly one place: every consumer of
 * this hook gets `isSuccess` for both "I computed a fresh snapshot" and "one
 * was already running", and can tell the two apart only by `data` being
 * non-null, never by `isError`.
 *
 * Either way, the GET query is told to refetch: on a genuine fresh snapshot
 * the new data is written straight into its cache (no extra round trip); on
 * a 409 the GET query is invalidated so it re-reads whatever the concurrent
 * refresh has left in place, since our own POST carried no new data to use. */
export function useTierActivityRefresh() {
  const qc = useQueryClient()
  return useMutation<S.TierActivity | null, Error, void>({
    mutationFn: async () => {
      try {
        return await post('/metrics/tier-activity/refresh', S.TierActivity)
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) return null
        throw e
      }
    },
    onSuccess: (data) => {
      if (data) qc.setQueryData(['tierActivity'], data)
      else void qc.invalidateQueries({ queryKey: ['tierActivity'] })
    },
  })
}

export function useVideoClassified() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['videoClassified'],
    queryFn: ({ signal }) => get('/metrics/videos/classified', S.ClassifiedCounts, signal),
    enabled: loaded, staleTime: 5 * 60_000, retry: 1,
  })
}

export function useVideoDistributions() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['videoDistributions'],
    queryFn: ({ signal }) => get('/metrics/videos/distributions?top=10', S.VideoDistributions, signal),
    enabled: loaded, staleTime: 5 * 60_000, retry: 1,
  })
}

export function useVideoDaily() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['videoDaily'],
    queryFn: ({ signal }) => get('/metrics/videos/daily?days=30', S.ClassifierDaily, signal),
    enabled: loaded, staleTime: 5 * 60_000, retry: 1,
  })
}

export const useDivergence = make('divergence', '/metrics/divergence', S.Divergence)

/* --- channel classification (mirrors-classifier) ------------------------- */
export function useClassifiedCounts() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['classifiedCounts'],
    queryFn: ({ signal }) => get('/metrics/classified', S.ClassifiedCounts, signal),
    enabled: loaded, refetchInterval: false, refetchOnMount: false,
    refetchOnReconnect: false, staleTime: CACHE_MS,
  })
}

export function useClassifierDistributions() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['classifierDistributions'],
    queryFn: ({ signal }) => get('/metrics/distributions?top=15', S.ClassifierDistributions, signal),
    enabled: loaded, refetchInterval: false, refetchOnMount: false,
    refetchOnReconnect: false, staleTime: CACHE_MS,
  })
}

export function useClassifierDaily() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['classifierDaily'],
    queryFn: ({ signal }) => get('/metrics/daily?days=30', S.ClassifierDaily, signal),
    enabled: loaded, refetchInterval: false, refetchOnMount: false,
    refetchOnReconnect: false, staleTime: CACHE_MS,
  })
}

/**
 * Live throughput. THE ONE HOOK THAT DOES NOT CACHE.
 *
 * Everything else on this board is a report you pull and hold for a day, and
 * that is deliberate -- the expensive panels scan hundreds of millions of
 * documents. Throughput is the exception because the question it answers
 * ("is work moving RIGHT NOW?") has no meaning at day-old resolution.
 *
 * The rate is computed server-side from two samples of a cumulative counter,
 * so pressing Refresh IS the sampling action: the first press establishes a
 * baseline and reports "—", the second reports the rate over the interval
 * between them. Caching would break that outright -- a cached answer would
 * show the same number however long an operator stared at it.
 */
function live<T extends import('zod').ZodTypeAny>(key: string, path: string, schema: T) {
  return () => {
    const { loaded } = useLoad()
    return useQuery({
      queryKey: [key],
      queryFn: ({ signal }) => get(path, schema, signal),
      enabled: loaded,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Zero, not CACHE_MS: a refetch must actually reach the server.
      staleTime: 0,
      gcTime: CACHE_MS,
      placeholderData: (prev) => prev,
      retry: 1,
    })
  }
}

export const useThroughput = live('throughput', '/metrics/throughput', S.Throughput)
export const useClassifierThroughput =
  live('clsThroughput', '/metrics/classifier/throughput', S.Throughput)

/* --- vidiq (SIL-10670) ---------------------------------------------------
 * Two hooks, deliberately: the counts are aggregates over a large table and
 * are cached like every other report, while the rate is live and uncached for
 * the reason `live()` explains. Folding them into one query would force the
 * expensive half to be re-read every time somebody wanted the cheap half. */
export const useVidiqCounts =
  make('vidiqCounts', '/metrics/vidiq', S.VidiqCounts)

/* Per-tier freshness. A plain cached read like the rest: the server is already
 * serving a snapshot, so polling harder would only re-read the same six rows —
 * the figures change when the coverage role captures, not when we ask. */
export const useVidiqTiers =
  make('vidiqTiers', '/metrics/vidiq/tiers', S.VidiqTiers)

/* --- embeddings (DeepInfraMapper) -----------------------------------------
 * One endpoint serves coverage, cost across every trailing window, the
 * 30-day daily series and all three breakdowns together, so one cached hook
 * is enough for the whole tab -- there is no per-range refetch the way ES
 * activity has, because the server already computed every cost band this
 * panel will ever need in a single response. */
export const useEmbeddings =
  make('embeddings', '/metrics/embeddings', S.Embeddings)

/**
 * Pipeline health — per-worker-role DB query latency and per-queue consumer
 * lag. THE ONE OTHER HOOK THAT POLLS, alongside `live()`'s manual-refresh
 * pair above, and for a related but distinct reason.
 *
 * `live()` (Throughput, classifier throughput) is manual: pressing Refresh
 * IS the sampling action, because the server computes a rate from two
 * counter reads taken exactly when asked. `/metrics/pipeline-health` is
 * different — the CONTRACT states it is deliberately uncached, no
 * `cached`/`generated_at` wrapper, always answering with whatever is true
 * right now — so there is no "press again to get a second sample" step for
 * this endpoint the way there is for a rate. A manual-refresh-only panel here
 * would silently go stale the moment nobody is looking at the tab, which is
 * exactly the failure this panel exists to close: a multi-day throughput
 * collapse went completely invisible because nothing on this board surfaced
 * "the database is degraded" as its own signal, and a panel that only
 * updates when clicked would have caught it no sooner than the ES dashboard
 * someone happened to check.
 *
 * This is a genuine departure from this file's own "Nothing polls" module
 * doctrine above, and deliberately so: that doctrine exists because most
 * panels here scan hundreds of millions of documents server-side, and
 * re-running that on a timer would cost far more than it tells anyone. A
 * Redis consumer-group read and a rolling latency histogram read are neither
 * — the same reason the collector's own contract calls this route out as
 * uncached in the first place. 10s keeps the panel visibly live without
 * hammering the endpoint on an idle tab.
 */
export function usePipelineHealth() {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['pipelineHealth'],
    queryFn: ({ signal }) => get('/metrics/pipeline-health', S.PipelineHealth, signal),
    enabled: loaded,
    refetchInterval: 10_000,
    // Do not keep polling an endpoint nobody is looking at — the same
    // "no live rate to a page nobody is looking at" reasoning the module
    // doctrine states, applied to a timer instead of a bare-eligibility flag.
    refetchIntervalInBackground: false,
    // Zero, not CACHE_MS: every tick must actually reach the server, the same
    // reason `live()` above sets staleTime 0 rather than trusting the cache.
    staleTime: 0,
    gcTime: CACHE_MS,
    placeholderData: (prev) => prev,
    retry: 1,
  })
}

/* ------------------------------------------------------------------ qdrant
 * THE ONE POLLING EXCEPTION ON THIS BOARD, and why it is safe where the
 * others are not: the no-poll premise above exists because the heaviest
 * panels scan hundreds of millions of ES/PG documents. The qdrant endpoints
 * are bounded Mongo aggregations over a 7-day TTL'd store with a 30 s
 * server-side cache — polling costs one aggregation set per minute TOTAL,
 * regardless of viewers. The user asked for a live view of the search
 * gateway, and a 24 h answer about a circuit breaker is not an answer.
 * Nothing here touches the QdrantMapper API itself — Mongo only. */
const QDRANT_POLL_MS = 60_000

function useQdrantQuery<T extends import('zod').ZodTypeAny>(
  key: readonly unknown[], path: string, schema: T,
) {
  const { loaded } = useLoad()
  return useQuery({
    queryKey: key,
    queryFn: ({ signal }) => get(path, schema, signal),
    enabled: loaded,
    refetchInterval: QDRANT_POLL_MS,
    refetchOnWindowFocus: false,
    staleTime: QDRANT_POLL_MS - 5_000,
    gcTime: 10 * 60_000,
    placeholderData: (prev) => prev,
    retry: 1,
  })
}

export function useQdrantOverview() {
  return useQdrantQuery(['qdrant', 'overview'], '/metrics/qdrant/overview',
    S.QdrantOverview)
}
export function useQdrantThroughput(window: string) {
  return useQdrantQuery(['qdrant', 'throughput', window],
    `/metrics/qdrant/throughput?window=${window}`, S.QdrantThroughput)
}
export function useQdrantLatency(window: string) {
  return useQdrantQuery(['qdrant', 'latency', window],
    `/metrics/qdrant/latency?window=${window}`, S.QdrantLatency)
}
export function useQdrantStatus(window: string) {
  return useQdrantQuery(['qdrant', 'status', window],
    `/metrics/qdrant/status?window=${window}`, S.QdrantStatus)
}
export type QdrantTopParams = {
  window: string; lane: string; status: string; sort: string; dir: string
  collection: string; endpoint: string; client: string
}
export function useQdrantTopQueries(p: QdrantTopParams) {
  const qs = new URLSearchParams({ window: p.window, sort: p.sort, dir: p.dir })
  if (p.lane) qs.set('lane', p.lane)
  if (p.status && p.status !== 'all') qs.set('status', p.status)
  if (p.collection) qs.set('collection', p.collection)
  if (p.endpoint) qs.set('endpoint', p.endpoint)
  if (p.client) qs.set('client', p.client)
  return useQdrantQuery(['qdrant', 'top', p],
    `/metrics/qdrant/top-queries?${qs}`, S.QdrantTopQueries)
}
export function useQdrantCluster(window: string) {
  return useQdrantQuery(['qdrant', 'cluster', window],
    `/metrics/qdrant/cluster?window=${window}`, S.QdrantCluster)
}
export function useQdrantClients(window: string) {
  return useQdrantQuery(['qdrant', 'clients', window],
    `/metrics/qdrant/clients?window=${window}`, S.QdrantClients)
}

/* ---------------------------------------------------------------------- TTD
 * Served by the collector, exactly like every other tab (`/metrics/ttd/*`,
 * collector/api/ttd_metrics.py, mirrors-collector repo) — no dashboard-side
 * proxy or backend of its own. That router reads the TTD content pipeline's
 * own Postgres/ClickHouse DIRECTLY, with its own credentials, rather than
 * calling the colleague-owned `ttd_backend_api` repo's own service. All
 * hooks are cached reports over a date range, `make()`-shaped: no polling,
 * held for a day like the rest of the board.
 *
 * Every hook shares the same `useRange()` (1/2/7/30 days) the collector's own
 * ES-activity panels use — one global chip drives both data sources, so
 * switching it re-scopes the whole board including this tab. */
const todayISO = () => new Date().toISOString().slice(0, 10)
const daysAgoISO = (days: number) => {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - days)
  return d.toISOString().slice(0, 10)
}

function ttdRangeQuery<T extends import('zod').ZodTypeAny>(key: string, path: string, schema: T) {
  return () => {
    const { loaded } = useLoad()
    const { days } = useRange()
    const dateFrom = daysAgoISO(days)
    const dateTo = todayISO()
    return useQuery({
      queryKey: [key, dateFrom, dateTo],
      queryFn: ({ signal }) =>
        get(`${path}?date_from=${dateFrom}&date_to=${dateTo}`, schema, signal),
      enabled: loaded,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: CACHE_MS,
      gcTime: CACHE_MS,
      placeholderData: (prev) => prev,
      retry: 2,
    })
  }
}

export const useTtdAssetPipeline =
  ttdRangeQuery('ttdAssetPipeline', '/metrics/ttd/asset-pipeline', S.TtdAssetPipeline)
export const useTtdIrisAssetPipeline =
  ttdRangeQuery('ttdIrisAssetPipeline', '/metrics/ttd/iris-asset-pipeline', S.TtdIrisAssetPipeline)
export const useTtdSourceData =
  ttdRangeQuery('ttdSourceData', '/metrics/ttd/source-data', S.TtdSourceData)
export const useTtdCtvDaily =
  ttdRangeQuery('ttdCtvDaily', '/metrics/ttd/ctv-daily', S.TtdCtvDaily)
export const useTtdCtvMatchedbyDaily =
  ttdRangeQuery('ttdCtvMatchedbyDaily', '/metrics/ttd/ctv-matchedby-daily', S.TtdCtvMatchedbyDaily)
export const useTtdCtvMatchedbyBuckets =
  ttdRangeQuery('ttdCtvMatchedbyBuckets', '/metrics/ttd/ctv-matchedby-buckets', S.TtdCtvMatchedbyBuckets)
export const useTtdCtvStats =
  ttdRangeQuery('ttdCtvStats', '/metrics/ttd/ctv-stats', S.TtdCtvStats)

/* ------------------------------------------------------------------ Scoring
 * Served by the collector (`/metrics/scoring/*`, collector/api/scoring_metrics.py,
 * mirrors-collector repo) like every other tab. All five are cached reports,
 * `make()`-shaped: no polling, held for a day, fetched only once Load is
 * pressed. `/overview` is uncached SERVER-side (a cached freshness reading is a
 * contradiction), so what the board shows is "as of Load/Refresh", the same
 * honesty as the vidIQ and throughput panels — Refresh re-reads it.
 *
 * Only outcomes is range-scoped: it shares the board's 1/2/7/30-day chip, and a
 * new window is a new query key (fetched once, then held). The jobs list is a
 * fixed 20 rows — the last N of a tiny table.
 *
 * DEPLOY ORDER — COLLECTOR FIRST. Every Scoring schema (api/schemas.ts) is
 * strict: a field the collector does not send fails Zod, the panel reads
 * "unavailable" and the rail goes crit. So:
 *  1. The collector branch `feature/scoring-metrics` (>= e8445c3) must already
 *     be live, with SCORING_MONGO_URL set (without it every queue figure is
 *     null and `queue.error` is set — blind, not broken).
 *  2. Pre-deploy check: `GET /metrics/scoring/overview` returns 200 and its body
 *     carries `queue.oldest_pending_s` and `freshness.failed_s`, the two newest
 *     required fields. A collector that predates them would blank every panel
 *     that reads /overview.
 *  3. The enums are closed on purpose (finding `sev`: crit | warn | info;
 *     overview `state`: crit | warn | ok | idle). A NEW backend `sev` or `state`
 *     value is a breaking change and needs a coordinated dashboard release —
 *     ship the schema change here before the collector starts sending it. */
export const useScoringOverview =
  make('scoringOverview', '/metrics/scoring/overview', S.ScoringOverview)
export const useScoringProgress =
  make('scoringProgress', '/metrics/scoring/progress', S.ScoringProgress)
export const useScoringJobs =
  make('scoringJobs', '/metrics/scoring/jobs?limit=20', S.ScoringJobs)
export const useScoringFailures =
  make('scoringFailures', '/metrics/scoring/failures', S.ScoringFailures)

export function useScoringOutcomes() {
  const { days } = useRange()
  const { loaded } = useLoad()
  return useQuery({
    queryKey: ['scoringOutcomes', days],
    queryFn: ({ signal }) =>
      get(`/metrics/scoring/outcomes?days=${days}`, S.ScoringOutcomes, signal),
    enabled: loaded,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: CACHE_MS,
    gcTime: CACHE_MS,
    placeholderData: (prev) => prev,
    retry: 2,
  })
}
